import React, { useRef, useState } from 'react'
import { Formik, Form } from 'formik'
import { useTranslation } from 'react-i18next'
import { Box, TextField } from '@material-ui/core'
import { ArrowUpward, AttachFile } from '@material-ui/icons'
import { useSnackbar } from 'notistack'
import { v4 as uuidv4 } from 'uuid'
import { ChatAttachmentList } from 'components/data/ChatAttachmentList/ChatAttachmentList'
import { ChatActionItem } from 'components/inputs/ChatActionItem/ChatActionItem'
import { ChatComposerActions } from 'components/inputs/ChatComposerActions/ChatComposerActions'
import { ChatInputSchema, ChatInputValues } from 'validations/ChatInputSchema'
import { toChatAttachment } from 'utils/toChatAttachment'
import { isChatAttachmentLimitReached, selectChatAttachmentFiles } from 'utils/chatAttachmentLimits'
import * as Styled from './ChatComposer.styles'

interface ChatComposerProps {
  isSending: boolean
  hasMessages: boolean
  onSend: (values: ChatInputValues) => Promise<void>
}

const initialValues: ChatInputValues = {
  message: '',
  attachments: [],
}

const ChatComposer: React.FC<ChatComposerProps> = React.memo(({
  isSending,
  hasMessages,
  onSend,
}) => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [focused, setFocused] = useState(false)
  const inputCardRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleAttachFile = () => {
    if (!isSending) fileInputRef.current?.click()
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={ChatInputSchema}
      onSubmit={async (values, { resetForm }) => {
        try {
          await onSend({ ...values, message: values.message.trim() })
          resetForm()
        } catch {
          // The mutation displays the error; keep this draft for retry.
        }
      }}
    >
      {({ handleChange, handleBlur, isValid, dirty, values, errors, setFieldValue }) => (
        <Styled.Form as={Form}>
          {!hasMessages && (
            <Styled.GreetingTitle variant='h2'>{t('aiChat.greeting')}</Styled.GreetingTitle>
          )}

          <Styled.InputCardAnchor ref={inputCardRef}>
            <Styled.InputCard $focused={focused}>
              <Styled.HiddenFileInput
                ref={fileInputRef}
                type='file'
                multiple
                disabled={isSending || isChatAttachmentLimitReached(values.attachments)}
                onChange={(event) => {
                  const { files, errorKeys } = selectChatAttachmentFiles(
                    values.attachments,
                    Array.from(event.target.files || []),
                  )

                  errorKeys.forEach((key) => enqueueSnackbar(t(key), { variant: 'error' }))
                  if (files.length) {
                    setFieldValue('attachments', [
                      ...values.attachments,
                      ...files.map((file) => ({ id: uuidv4(), file })),
                    ])
                  }
                  event.target.value = ''
                }}
              />
              <ChatAttachmentList
                attachments={values.attachments.map(toChatAttachment)}
                onRemoveAttachment={(id) => {
                  setFieldValue(
                    'attachments',
                    values.attachments.filter((attachment) => attachment.id !== id),
                  )
                }}
                disabled={isSending}
              />
              <Styled.InputRow>
                <ChatComposerActions
                  containerRef={inputCardRef}
                  hasMessages={hasMessages}
                  disabled={isSending || isChatAttachmentLimitReached(values.attachments)}
                  label={t('common.attachFile')}
                >
                  <ChatActionItem
                    icon={<AttachFile fontSize='small' />}
                    label={t('common.attachFile')}
                    onClick={handleAttachFile}
                  />
                </ChatComposerActions>
                <TextField
                  name='message'
                  fullWidth
                  variant='standard'
                  placeholder={t('aiChat.inputPlaceholder')}
                  onChange={handleChange}
                  value={values.message}
                  onBlur={(e) => {
                    setFocused(false)
                    handleBlur(e)
                  }}
                  onFocus={() => setFocused(true)}
                  disabled={isSending}
                  error={!!errors.message}
                  helperText={errors.message && t(errors.message)}
                  multiline
                  maxRows={4}
                  InputProps={{ disableUnderline: true }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      if (isValid && dirty && !isSending) {
                        const form = e.currentTarget.closest('form')
                        form?.requestSubmit()
                      }
                    }
                  }}
                />
                <Box>
                  <Styled.SendButton
                    type='submit'
                    disabled={isSending}
                    size='small'
                    aria-label={t('aiChat.send')}
                  >
                    <ArrowUpward fontSize='small' />
                  </Styled.SendButton>
                </Box>
              </Styled.InputRow>
            </Styled.InputCard>
          </Styled.InputCardAnchor>
        </Styled.Form>
      )}
    </Formik>
  )
})

export { ChatComposer }
